/**
 * Gap Understanding Modal
 * Educational modal explaining the three exit readiness gaps
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Target, TrendingUp, X } from 'lucide-react';

const GapUnderstandingModal = ({ show, onClose }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4 sm:p-6 rounded-t-xl sm:rounded-t-2xl flex items-center justify-between z-10">
          <h2 className="text-lg sm:text-2xl font-bold pr-2">Understanding Your Business Exit Gaps</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
            aria-label="Close"
          >
            <X size={24} className="sm:w-7 sm:h-7" />
          </button>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          <p className="text-gray-700 mb-6 text-lg">
            These three gaps provide a comprehensive view of your business's readiness for a successful exit. Each gap represents a different aspect of exit planning and offers unique opportunities for improvement.
          </p>

          <div className="space-y-6">
            {/* Wealth Gap Narrative */}
            <div className="bg-white rounded-xl p-6 border-l-4 border-orange-500 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Target className="text-orange-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Wealth Gap</h3>
                  <p className="text-sm text-gray-600">Your Financial Independence Target</p>
                </div>
              </div>
              <div className="space-y-3 text-gray-700">
                <p>
                  <span className="font-semibold">What it means:</span> The Wealth Gap represents the difference between your current net worth and the wealth you need to achieve financial independence after exiting your business. This gap shows you exactly how much value you need to extract from your business exit to maintain your desired lifestyle without relying on future income.
                </p>
                <p>
                  The calculation factors in your desired annual lifestyle expenses, expected investment returns, inflation, life expectancy, and any other income sources to determine your precise wealth target. A positive gap means you need to build more wealth before exit, while a negative gap (surplus) means you've already exceeded your target.
                </p>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-3">
                  <p className="font-semibold text-orange-900 mb-2">How to improve:</p>
                  <ul className="space-y-2 text-sm text-orange-900">
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 mt-1">•</span>
                      <span><span className="font-semibold">Increase business value:</span> Focus on growing revenue, improving profitability, and implementing the improvements identified in your Value Gap and Profit Gap analyses</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 mt-1">•</span>
                      <span><span className="font-semibold">Diversify wealth:</span> Build assets outside the business through strategic investments, real estate, or other income-producing opportunities</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 mt-1">•</span>
                      <span><span className="font-semibold">Optimize exit timing:</span> Consider waiting to exit when your business value peaks, or pursue a staged exit strategy to maximize proceeds</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 mt-1">•</span>
                      <span><span className="font-semibold">Reduce lifestyle costs:</span> If appropriate, adjusting your post-exit lifestyle expectations can significantly reduce your wealth target</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-600 mt-1">•</span>
                      <span><span className="font-semibold">Create additional income streams:</span> Develop consulting opportunities, board positions, or passive income sources to supplement your wealth and reduce dependence on exit proceeds</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Profit Gap Narrative */}
            <div className="bg-white rounded-xl p-6 border-l-4 border-purple-500 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Profit Gap</h3>
                  <p className="text-sm text-gray-600">Your EBITDA Performance Opportunity</p>
                </div>
              </div>
              <div className="space-y-3 text-gray-700">
                <p>
                  <span className="font-semibold">What it means:</span> The Profit Gap analyzes your business value potential based on EBITDA margin improvements while keeping your current assessment score constant. It shows two scenarios: reaching the <span className="font-semibold">Median Industry Margin</span> and reaching the <span className="font-semibold">Best in Class Margin</span> in your industry.
                </p>
                <p>
                  Since most businesses are valued as a multiple of EBITDA, improving your EBITDA margin has a compounding effect on your exit value. The calculation uses your current Overall Business Gap Assessment Score and scales your valuation range proportionally based on EBITDA changes. For example, if you increase EBITDA by 20%, your entire valuation range scales up by 20%, and your position within that range (determined by your assessment score) gives you the final valuation. This shows you exactly how much additional value you can unlock through operational efficiency and profitability improvements.
                </p>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-3">
                  <p className="font-semibold text-purple-900 mb-2">How to improve:</p>
                  <ul className="space-y-2 text-sm text-purple-900">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span><span className="font-semibold">Analyze cost structure:</span> Conduct a detailed review of all expenses to identify areas where you're overspending compared to industry benchmarks</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span><span className="font-semibold">Optimize operations:</span> Streamline processes, eliminate inefficiencies, and implement automation to reduce operational costs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span><span className="font-semibold">Improve pricing strategy:</span> Review your pricing model to ensure you're capturing appropriate value, especially if you're providing premium service or products</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span><span className="font-semibold">Renegotiate vendor contracts:</span> Leverage your purchasing power to secure better terms with suppliers and service providers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span><span className="font-semibold">Focus on high-margin offerings:</span> Shift your business mix toward products or services that generate higher margins and reduce or eliminate low-margin offerings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span><span className="font-semibold">Benchmark against peers:</span> Study how top performers in your industry achieve superior margins and adapt their best practices to your business</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Value Gap Narrative */}
            <div className="bg-white rounded-xl p-6 border-l-4 border-emerald-500 shadow-sm">
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <TrendingUp className="text-emerald-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Value Gap</h3>
                  <p className="text-sm text-gray-600">Your Business Attractiveness and Readiness Impact</p>
                </div>
              </div>
              <div className="space-y-3 text-gray-700">
                <p>
                  <span className="font-semibold">What it means:</span> The Value Gap analyzes your business value potential based on assessment score improvements while keeping your current EBITDA margin constant. It shows two scenarios: reaching the <span className="font-semibold">Green Zone Score (67%)</span> and reaching <span className="font-semibold">Best in Class Status (72%)</span> based on the 8 Pillars of Business Attractiveness and Exit Readiness.
                </p>
                <p>
                  Your business attractiveness and readiness score determines your position within the valuation range on a scale from 17% (minimum) to 100% (maximum), where a 58% score represents an average business at approximately the midpoint. The Green Zone (67%) represents a solid, well-prepared business at the 60th percentile, while Best in Class (72%) positions your business at the 66th percentile, commanding premium valuations. Each improvement in your assessment score moves you higher within your valuation range without changing your EBITDA, demonstrating the pure value of business quality, transferability, and exit readiness.
                </p>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-3">
                  <p className="font-semibold text-emerald-900 mb-2">How to improve:</p>
                  <ul className="space-y-2 text-sm text-emerald-900">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-1">•</span>
                      <span><span className="font-semibold">Reduce customer concentration:</span> Diversify your customer base so no single client represents more than 10-15% of revenue, reducing risk for potential buyers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-1">•</span>
                      <span><span className="font-semibold">Build management independence:</span> Develop a strong leadership team that can run the business without your daily involvement, proving the business isn't dependent on you</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-1">•</span>
                      <span><span className="font-semibold">Document systems and processes:</span> Create comprehensive SOPs, training manuals, and operational playbooks that ensure business continuity and transferability</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-1">•</span>
                      <span><span className="font-semibold">Demonstrate recurring revenue:</span> Build predictable cash flow through subscriptions, contracts, retainers, or other recurring revenue models that reduce buyer risk</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-1">•</span>
                      <span><span className="font-semibold">Establish growth trajectory:</span> Show consistent year-over-year growth in revenue and profitability with a clear strategy for continued expansion</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-1">•</span>
                      <span><span className="font-semibold">Complete the Gap Analysis assessment:</span> Review your scores across all 8 Pillars, prioritize the areas with the lowest scores, and systematically address each weakness</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-1">•</span>
                      <span><span className="font-semibold">Review tasks in Task Manager:</span> Use the personalized action items generated from your assessment to implement specific improvements in priority order</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Integrated Action Plan */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-6 text-white shadow-lg">
              <h3 className="text-xl font-bold mb-3">Integrated Action Plan</h3>
              <p className="mb-4 text-blue-50">
                These three gaps work together to define your path to exit readiness. Each provides a unique perspective on value creation:
              </p>
              <div className="space-y-3 text-sm text-blue-50">
                <p className="flex items-start gap-2">
                  <span className="font-bold text-white mt-0.5">1.</span>
                  <span><span className="font-semibold text-white">Wealth Gap</span> - Start by understanding your financial target. This sets your overall goal and timeline, helping you determine how much value you need to extract from your business exit to achieve financial independence.</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="font-bold text-white mt-0.5">2.</span>
                  <span><span className="font-semibold text-white">Profit Gap</span> - Work on improving your EBITDA margins to industry benchmarks (Median or Best in Class). This analysis holds your assessment score constant and shows how operational efficiency directly scales your valuation range, increasing your business value through improved profitability.</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="font-bold text-white mt-0.5">3.</span>
                  <span><span className="font-semibold text-white">Value Gap</span> - Simultaneously improve your business attractiveness and exit readiness score to reach Green Zone (67%) or Best in Class (72%) status. This analysis holds your EBITDA constant and shows how improvements in business quality, transferability, and readiness move you to higher positions within your valuation range.</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="font-bold text-white mt-0.5">4.</span>
                  <span><span className="font-semibold text-white">Combined Impact</span> - The magic happens when you improve both dimensions simultaneously. Higher EBITDA margins scale your valuation range upward, while a better assessment score moves you to a higher position within that scaled range, creating a multiplier effect on your business value.</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="font-bold text-white mt-0.5">5.</span>
                  <span>Regularly recalculate all three gaps to track progress. As you improve profitability and attractiveness, both your valuation and exit readiness increase, moving you closer to achieving your wealth target.</span>
                </p>
              </div>
              <div className="mt-5 pt-5 border-t border-blue-400">
                <p className="text-sm text-blue-100 italic">
                  Remember: Closing these gaps takes time. Start early, focus on high-impact improvements in both operations (Profit Gap) and business quality (Value Gap), and use the Task Manager to stay organized. The businesses that command the highest valuations are those that systematically address all three areas well before going to market.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition min-h-[44px]"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

GapUnderstandingModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default GapUnderstandingModal;
